import { useEffect, useState } from "react";
import { employeeReferralInfo, employeeMyAgents } from "@/api";
import { toast } from "sonner";
import { Copy, UserCog, TrendingUp, Loader2, Ticket, CheckCircle2, Clock, XCircle, MessageCircle } from "lucide-react";

function StatusPill({ status }) {
  const map = {
    approved: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: "Approved" },
    pending:  { cls: "bg-amber-50 text-amber-700 border-amber-200",     icon: Clock,         label: "Pending" },
    rejected: { cls: "bg-red-50 text-red-700 border-red-200",           icon: XCircle,       label: "Rejected" },
  };
  const m = map[status] || { cls: "bg-slate-50 text-slate-600 border-slate-200", icon: Clock, label: status || "—" };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

export default function EmployeeMyReferrals() {
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState({ referral_code: null, total_agents_referred: 0 });
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [r1, r2] = await Promise.all([employeeReferralInfo(), employeeMyAgents()]);
        setInfo({
          referral_code: r1.data?.referral_code || null,
          total_agents_referred: r1.data?.total_agents_referred || 0,
        });
        setAgents(r2.data?.agents || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load referrals");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copyCode = () => {
    if (!info.referral_code) return;
    navigator.clipboard.writeText(info.referral_code);
    toast.success("Referral code copied");
  };

  // Build a shareable WhatsApp link with a pre-written message and an
  // /agent-register?ref=CODE deep link so the prospective agent doesn't
  // have to type the referral code manually.
  const shareOnWhatsApp = () => {
    if (!info.referral_code) return;
    const registerUrl = `${window.location.origin}/agent-register?ref=${encodeURIComponent(info.referral_code)}`;
    const message =
      `Hey! I'm working with *Rebuilt* — India's EMI assistance platform that helps customers stay on top of their loan EMIs.\n\n` +
      `We're looking for field agents who want to earn good commissions. Quick application, no upfront cost.\n\n` +
      `Use my referral code: *${info.referral_code}*\n` +
      `Or sign up directly here:\n${registerUrl}\n\n` +
      `Reach out if you have any questions!`;
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employee-my-referrals">
      <div>
        <h2 className="text-3xl font-black text-[#0A2540] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          My Referrals
        </h2>
        <p className="text-sm text-[#6B7280] mt-1">Share your code with prospective agents. You'll see anyone who signs up using it right here.</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-[#1565C0] to-[#0D47A1] rounded-2xl p-6 shadow-lg text-white">
          <div className="flex items-center gap-2 mb-3">
            <Ticket className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold opacity-90">My Referral Code</span>
          </div>
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : info.referral_code ? (
            <div className="flex items-center gap-3 flex-wrap">
              <code
                className="text-2xl font-black font-mono bg-white/15 backdrop-blur px-4 py-2 rounded-xl border border-white/20 tracking-wider"
                data-testid="employee-referral-code"
              >
                {info.referral_code}
              </code>
              <button
                onClick={copyCode}
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/20 hover:bg-white/30 transition-colors px-3 py-2 rounded-lg"
                data-testid="copy-referral-code"
              >
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
              <button
                onClick={shareOnWhatsApp}
                className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#25D366] hover:bg-[#1ebe5d] text-white transition-colors px-3 py-2 rounded-lg shadow-md"
                data-testid="share-referral-whatsapp"
                title="Share via WhatsApp"
              >
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
              </button>
            </div>
          ) : (
            <p className="text-sm opacity-90">No code assigned yet. Contact admin.</p>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Agents Referred</span>
          </div>
          <p
            className="text-4xl font-black text-[#0A2540]"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="employee-total-referred"
          >
            {loading ? <Loader2 className="h-7 w-7 animate-spin text-[#1565C0]" /> : (info.total_agents_referred || 0)}
          </p>
          <p className="text-xs text-slate-500 mt-1">Total agents onboarded using your code</p>
        </div>
      </div>

      {/* Agents table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <UserCog className="h-4 w-4 text-[#1565C0]" />
          <h3 className="text-sm font-black text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>My Agent Referrals</h3>
        </div>
        {loading ? (
          <div className="p-16 flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-[#1565C0] animate-spin" />
          </div>
        ) : agents.length === 0 ? (
          <div className="p-16 text-center" data-testid="employee-referrals-empty">
            <UserCog className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">No agents have signed up with your code yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="employee-referrals-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                <tr>
                  <th className="text-left px-5 py-3">Agent</th>
                  <th className="text-left px-5 py-3">Mobile</th>
                  <th className="text-left px-5 py-3">City</th>
                  <th className="text-left px-5 py-3">Application Status</th>
                  <th className="text-left px-5 py-3">Agent Status</th>
                  <th className="text-left px-5 py-3">Referrals</th>
                  <th className="text-left px-5 py-3">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {agents.map(a => (
                  <tr key={a.id} className="hover:bg-slate-50/60" data-testid={`employee-ref-agent-${a.id}`}>
                    <td className="px-5 py-3">
                      <p className="font-bold text-slate-900">{a.name}</p>
                      <p className="text-[11px] text-slate-500">{a.email || '—'}</p>
                    </td>
                    <td className="px-5 py-3 font-mono text-slate-700">{a.phone || '—'}</td>
                    <td className="px-5 py-3 text-slate-600">{a.city || '—'}{a.state ? `, ${a.state}` : ''}</td>
                    <td className="px-5 py-3"><StatusPill status={a.application_status} /></td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${a.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        {a.status || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-black text-[#0A2540]">{a.total_referrals || 0}</td>
                    <td className="px-5 py-3 text-[11px] text-slate-500">{fmtDate(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
